import { initializeApp } from "firebase/app";
import { getAnalytics, isSupported, logEvent } from "firebase/analytics";

// REACT_APP_GA_TRACKING_ENABLED can be set to "false" (in .env) to disable all tracking.
const TRACKING_ENABLED = process.env.REACT_APP_GA_TRACKING_ENABLED !== "false";

// Which Firebase project (dev/prod) is used is chosen in .env by commenting/
// uncommenting the relevant REACT_APP_FIREBASE_* block.
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

let analyticsInstance = null;
let initPromise = null;

// Initializes Firebase + GA4. Safe to call multiple times (only runs once).
export function initAnalytics() {
  if (!TRACKING_ENABLED) return Promise.resolve(null);
  if (initPromise) return initPromise;

  if (!firebaseConfig.apiKey || !firebaseConfig.measurementId) {
    console.warn(
      "[analytics] Missing REACT_APP_FIREBASE_* config in .env — tracking disabled.",
    );
    return Promise.resolve(null);
  }

  initPromise = isSupported()
    .then((supported) => {
      if (!supported) return null;
      const app = initializeApp(firebaseConfig);
      analyticsInstance = getAnalytics(app);
      return analyticsInstance;
    })
    .catch(() => null);

  return initPromise;
}

export function trackEvent(eventName, params = {}) {
  if (!TRACKING_ENABLED || !analyticsInstance) return;
  logEvent(analyticsInstance, eventName, params);
}

export function trackPageView(path, title) {
  trackEvent("page_view", {
    screen_name: title || path,
    page_path: path,
    page_title: title,
    page_location: window.location.href,
  });
}

// Delegated click listener so every button/link on the site is tracked,
// including ones added later, without instrumenting each component.
let clickTrackingBound = false;

export function initButtonClickTracking() {
  if (!TRACKING_ENABLED || clickTrackingBound) return;
  clickTrackingBound = true;

  document.addEventListener(
    "click",
    (event) => {
      const target = event.target.closest(
        'button, a, [role="button"], input[type="submit"], input[type="button"]',
      );
      if (!target) return;

      const buttonName =
        target.dataset.gaLabel ||
        target.getAttribute("aria-label") ||
        target.innerText?.trim().slice(0, 100) ||
        target.id ||
        target.name ||
        "unnamed";

      trackEvent("button_click", {
        button_name: buttonName,
        screen_name: document.title,
        element_tag: target.tagName.toLowerCase(),
        href: target.getAttribute("href") || undefined,
        page_path: window.location.pathname,
      });
    },
    true,
  );
}
